import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ConfirmDialog from '../../components/confirm-dialog';
import { useNavigationBlocker } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { GableTruss, HeaderTruss, LeanToTruss, OrderStatus, UserRole } from '../../types';
import { DateUtils, getErrorMessage, logError } from '../../utils';
import { useGetDealerByIdQuery } from '../dealer';
import {
  ordersApi,
  useGetBuildingByIdQuery,
  useGetBuildingsByOrderIdQuery,
  useGetOrderByIdQuery,
  useUpdateBuildingMutation,
  useUpdateTravelerCompletionMutation,
} from '../order';
import { postSizeEnumToDisplay } from '../order/building-constants';
import {
  useGetActiveTechniciansByManufacturerQuery,
  useGetTechnicianByIdQuery,
} from '../technician';
import { selectCurrentUser } from '../user';
import BuildingCarousel from './components/building-carousel';
import CompletionModal from './components/completion-modal';
import ProgressSummary from './components/progress-summary';
import TravelerAddTrussDialog from './components/traveler-add-truss-dialog';
import TravelerFooter from './components/traveler-footer';
import TravelerJobDetails from './components/traveler-job-details';
import TravelerOrderHeader from './components/traveler-order-header';
import TravelerPageHeader from './components/traveler-page-header';
import TrussAccordionSection from './components/truss-accordion';
import {
  areAllTrussesThroughFinalQc,
  buildSpecsMap,
  buildTaskTrackerMap,
  computeBuildingCompletionStatuses,
  transformBuildingListToBasicTraveler,
  transformBuildingToTraveler,
  transformOrderToTravelerHeader,
} from './traveler-data-transformer';
import {
  buildingToCartUpdateRequest,
  validatePostSizeForTrusses,
  withRecalculatedInsidePostMeasurements,
} from './traveler-truss-cart-helpers';
import { ProductType, TravelerBuilding, TravelerTruss } from './types';
import {
  useCreateTaskTrackerMutation,
  useGetAllBuildingsWithTrussesQuery,
  useGetManufacturerStageConfigsQuery,
  useGetTaskTrackersByOrderQuery,
  useGetTrussSpecificationsForBuildingQuery,
  useUpdateTaskTrackerMutation,
  workOrderTravelerApi,
} from './work-order-traveler-api-slice';

const WorkOrderTravelerPage = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  // Fetch order data from existing endpoints
  const {
    data: order,
    isLoading: orderLoading,
    isError: orderError,
  } = useGetOrderByIdQuery(orderId!, { skip: !orderId });

  const { data: buildingsList = [], isLoading: buildingsLoading } = useGetBuildingsByOrderIdQuery(
    orderId!,
    { skip: !orderId }
  );

  const { data: dealer } = useGetDealerByIdQuery(order?.dealerId?.toString() ?? '', {
    skip: !order?.dealerId,
  });

  const manufacturerId =
    dealer?.manufacturerId ?? order?.engineeringFees?.manufacturerId ?? currentUser?.manufacturerId;

  const { data: activeTechnicians = [] } = useGetActiveTechniciansByManufacturerQuery(
    manufacturerId!,
    { skip: !manufacturerId }
  );

  const orderIdNum = orderId ? parseInt(orderId, 10) : 0;
  const { data: taskTrackers = [] } = useGetTaskTrackersByOrderQuery(orderIdNum, {
    skip: !orderIdNum,
  });
  const [updateTaskTracker] = useUpdateTaskTrackerMutation();
  const [createTaskTracker] = useCreateTaskTrackerMutation();
  const [updateBuilding] = useUpdateBuildingMutation();
  const [updateTravelerCompletion] = useUpdateTravelerCompletionMutation();

  const taskTrackerMap = useMemo(() => buildTaskTrackerMap(taskTrackers), [taskTrackers]);

  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [completionModalTruss, setCompletionModalTruss] = useState<TravelerTruss | null>(null);
  const [completionDate, setCompletionDate] = useState('');
  const [qcSignOffTechnicianId, setQcSignOffTechnicianId] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
  const [hasUnsavedStageEdits, setHasUnsavedStageEdits] = useState(false);
  const [addTrussDialogOpen, setAddTrussDialogOpen] = useState(false);
  const [addTrussProductType, setAddTrussProductType] = useState<ProductType | null>(null);
  const [trussToDeleteId, setTrussToDeleteId] = useState<number | null>(null);
  const [deleteTrussConfirmOpen, setDeleteTrussConfirmOpen] = useState(false);
  const [postSizeWarning, setPostSizeWarning] = useState<{
    show: boolean;
    message: string;
    pendingLists: { gable: GableTruss[]; header: HeaderTruss[]; leanTo: LeanToTruss[] } | null;
  }>({ show: false, message: '', pendingLists: null });

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useNavigationBlocker(hasUnsavedStageEdits);

  // Set initial selected building when buildings load
  useEffect(() => {
    if (buildingsList.length > 0 && !selectedBuildingId) {
      setSelectedBuildingId(buildingsList[0].id);
    }
  }, [buildingsList, selectedBuildingId]);

  // Initialize the traveler footer fields from the persisted order. Re-syncs whenever the
  // persisted values change (e.g. after a save invalidates and refetches the order).
  useEffect(() => {
    setQcSignOffTechnicianId(order?.qcSignOffBy != null ? String(order.qcSignOffBy) : '');
    setCompletionDate(order?.completionDate ? order.completionDate.split('T')[0] : '');
  }, [order?.id, order?.qcSignOffBy, order?.completionDate]);

  // Fetch full building details (with trusses) for the selected building
  const { data: selectedBuildingFull, isLoading: buildingDetailLoading } = useGetBuildingByIdQuery(
    selectedBuildingId?.toString() ?? '',
    {
      skip: !selectedBuildingId,
    }
  );

  // Fetch all buildings with trusses to compute completion statuses for the carousel
  const buildingIds = useMemo(() => buildingsList.map(b => b.id), [buildingsList]);
  const { data: allBuildingsFull = [] } = useGetAllBuildingsWithTrussesQuery(buildingIds, {
    skip: buildingIds.length === 0,
  });

  // Order-level QC sign-off gates buildings from showing "complete" on the traveler.
  const orderQcSignedOff = order?.qcSignOffBy != null;

  const qcSignOffTechnicianInactive =
    orderQcSignedOff &&
    !!qcSignOffTechnicianId &&
    !activeTechnicians.some(t => String(t.id) === String(qcSignOffTechnicianId));

  const { data: qcSignOffTechnicianRecord } = useGetTechnicianByIdQuery(qcSignOffTechnicianId, {
    skip: !qcSignOffTechnicianInactive,
  });

  const qcSignOffTechnicianName = qcSignOffTechnicianRecord
    ? `${qcSignOffTechnicianRecord.firstName} ${qcSignOffTechnicianRecord.lastName}`
    : null;

  // The WO footer QC sign-off can only be recorded once every truss has passed FINAL QC.
  const allTrussesThroughFinalQc = useMemo(
    () => areAllTrussesThroughFinalQc(allBuildingsFull, taskTrackerMap),
    [allBuildingsFull, taskTrackerMap]
  );

  const buildingStatusMap = useMemo(
    () => computeBuildingCompletionStatuses(allBuildingsFull, taskTrackerMap),
    [allBuildingsFull, taskTrackerMap]
  );

  const buildingsWithoutTrusses = useMemo(
    () =>
      allBuildingsFull.filter(
        b =>
          (b.gableTrusses?.length ?? 0) +
            (b.headerTrusses?.length ?? 0) +
            (b.leanToTrusses?.length ?? 0) ===
          0
      ),
    [allBuildingsFull]
  );

  // Fetch server-computed truss specifications for the selected building
  const { data: trussSpecs = [] } = useGetTrussSpecificationsForBuildingQuery(selectedBuildingId!, {
    skip: !selectedBuildingId,
  });

  const specsMap = useMemo(() => buildSpecsMap(trussSpecs), [trussSpecs]);

  // Fetch server-side production stage configuration for this manufacturer (UDOMI-456 / BR-17)
  const manufacturerIdNum = manufacturerId ?? 0;
  const { data: stageConfigs = [] } = useGetManufacturerStageConfigsQuery(manufacturerIdNum, {
    skip: !manufacturerIdNum,
  });

  // Permissions
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const isSupervisor = currentUser?.role === UserRole.SUPERVISOR;
  const isCSR = currentUser?.role === UserRole.CSR;
  const canSignOff = !isCSR && currentUser?.allowSignOffOnTasks === true;

  const canRecordStages = isAdmin || isSupervisor || canSignOff;
  const canManageTrusses = isAdmin || isSupervisor;
  const canExportPdf = isAdmin || isSupervisor || isCSR;

  const isOrderNotYetInProduction = !!order && order.status < OrderStatus.IN_QUEUE;
  const isOrderProductionLocked =
    !!order && (order.status === OrderStatus.ON_HOLD || order.status >= OrderStatus.COMPLETE);
  const effectiveCanRecordStages =
    canRecordStages && !isOrderProductionLocked && !isOrderNotYetInProduction;
  const effectiveCanManageTrusses = isAdmin || (canManageTrusses && !isOrderProductionLocked);
  const effectiveCanAdminOverride = isAdmin;

  // Transform order header
  const travelerHeader = useMemo(() => {
    if (!order) return null;
    return transformOrderToTravelerHeader(order, dealer?.name);
  }, [order, dealer]);

  // Build basic building cards for the carousel from the buildings list
  const travelerBuildingsBasic = useMemo(() => {
    if (!order || !buildingsList.length) return [];
    return transformBuildingListToBasicTraveler(buildingsList, order, buildingStatusMap);
  }, [buildingsList, order, buildingStatusMap]);

  // Transform the selected building's full data (with trusses + task trackers + API specs) for display
  const selectedBuilding: TravelerBuilding | null = useMemo(() => {
    if (!selectedBuildingFull || !order) return null;
    return transformBuildingToTraveler(
      selectedBuildingFull,
      order,
      taskTrackerMap,
      specsMap,
      stageConfigs
    );
  }, [selectedBuildingFull, order, taskTrackerMap, specsMap, stageConfigs]);

  const commitTrussCart = useCallback(
    async (
      g: GableTruss[],
      h: HeaderTruss[],
      l: LeanToTruss[],
      options?: { skipPostSize?: boolean }
    ): Promise<boolean> => {
      if (!selectedBuildingFull || !order) return false;
      // Admins always have unrestricted access to the traveler; only
      // non-admin roles are blocked when the order is on hold or complete.
      if (isOrderProductionLocked && !isAdmin) {
        setToastSeverity('error');
        setToastMessage('This order is not open for traveler edits.');
        return false;
      }
      const postSizeDisplay =
        postSizeEnumToDisplay[selectedBuildingFull.postSize] || selectedBuildingFull.postSize;
      const trussesForSave = withRecalculatedInsidePostMeasurements(
        { gable: g, header: h, leanTo: l },
        postSizeDisplay
      );
      const pv = validatePostSizeForTrusses(postSizeDisplay, trussesForSave);
      if (!pv.isValid && !options?.skipPostSize) {
        setPostSizeWarning({
          show: true,
          message: pv.warningMessage,
          pendingLists: trussesForSave,
        });
        return false;
      }
      // Block submission when inside measurement is invalid (posts wider than span).
      const allTrusses = [
        ...trussesForSave.gable,
        ...trussesForSave.header,
        ...trussesForSave.leanTo,
      ];
      if (allTrusses.some(t => t.measurementInsidePost <= 0)) {
        setToastSeverity('error');
        setToastMessage(
          'One or more trusses have an inside post-to-post measurement that is invalid. ' +
            'The posts are too wide for the selected truss span.'
        );
        return false;
      }
      try {
        const orderDate = order.orderDate || new Date().toISOString();
        const request = buildingToCartUpdateRequest(
          selectedBuildingFull,
          trussesForSave.gable,
          trussesForSave.header,
          trussesForSave.leanTo,
          orderDate
        );
        await updateBuilding({ buildingId: selectedBuildingFull.id, building: request }).unwrap();
        dispatch(
          workOrderTravelerApi.util.invalidateTags([
            { type: 'TrussSpecification', id: selectedBuildingFull.id },
            { type: 'TaskTracker', id: `ORDER-${orderIdNum}` },
            ...buildingIds.map(id => ({ type: 'TravelerBuildingTrusses' as const, id })),
          ])
        );
        setPostSizeWarning({ show: false, message: '', pendingLists: null });
        setToastSeverity('success');
        setToastMessage('Truss list updated');
        return true;
      } catch (err) {
        console.error(err);
        setToastSeverity('error');
        setToastMessage(getErrorMessage(err));
        return false;
      }
    },
    [
      selectedBuildingFull,
      order,
      updateBuilding,
      dispatch,
      buildingIds,
      orderIdNum,
      isOrderProductionLocked,
      isAdmin,
    ]
  );

  const handlePostSizeProceed = useCallback(() => {
    const p = postSizeWarning.pendingLists;
    if (!p) return;
    void commitTrussCart(p.gable, p.header, p.leanTo, { skipPostSize: true }).then(ok => {
      if (ok) {
        setAddTrussDialogOpen(false);
        setAddTrussProductType(null);
      }
    });
  }, [postSizeWarning.pendingLists, commitTrussCart]);

  const handleSaveStage = useCallback(
    async (
      trussId: number,
      stageId: number,
      data: { technicianId: string; date: string; initials: string }
    ) => {
      try {
        const allTrusses = selectedBuilding
          ? [
              ...selectedBuilding.gableTrusses,
              ...selectedBuilding.headerTrusses,
              ...selectedBuilding.leanToTrusses,
            ]
          : [];
        const truss = allTrusses.find(t => t.id === trussId);
        const stage = truss?.productionStages.find(s => s.id === stageId);

        if (!stage) return;

        // Technician ids arrive from the API as numbers but are modeled as strings
        // throughout the traveler, so compare loosely to avoid a failed lookup that
        // would wipe the stored technician name (UDOMI-1004).
        const selectedTech = activeTechnicians.find(
          t => String(t.id) === String(data.technicianId)
        );
        const techName = selectedTech
          ? `${selectedTech.firstName} ${selectedTech.lastName}`
          : stage.technicianName;

        if (order && order.status < OrderStatus.IN_QUEUE) {
          setToastSeverity('error');
          setToastMessage('This order has not been released to production yet.');
          return;
        }
        if (
          order &&
          (order.status === OrderStatus.ON_HOLD || order.status >= OrderStatus.COMPLETE)
        ) {
          setToastSeverity('error');
          setToastMessage('This order is not open for traveler edits.');
          return;
        }

        const requestPayload = {
          trussId,
          stageName: stage.stageName,
          sequenceOrder: stage.stageOrder,
          technicianId: parseInt(data.technicianId, 10) || null,
          technicianName: techName,
          initials: data.initials,
          completedDate: DateUtils.localDateInputToUtcIso(data.date),
          status: 'Complete' as const,
        };

        if (stageId < 0) {
          await createTaskTracker(requestPayload).unwrap();
        } else {
          await updateTaskTracker({ id: stageId, request: requestPayload }).unwrap();
        }
        dispatch(ordersApi.util.invalidateTags(['Dashboard']));
        setToastSeverity('success');
        setToastMessage(`✓ ${stage.stageName} saved`);
      } catch (err) {
        console.error('Failed to save production stage:', err);
        setToastSeverity('error');
        setToastMessage('Failed to save stage. Please try again.');
        throw err;
      }
    },
    [
      activeTechnicians,
      selectedBuilding,
      order,
      updateTaskTracker,
      createTaskTracker,
      isAdmin,
      dispatch,
    ]
  );

  const handleFinalQcComplete = useCallback((truss: TravelerTruss) => {
    setCompletionModalTruss(truss);
  }, []);

  const [isSavingFooter, setIsSavingFooter] = useState(false);

  const persistTravelerCompletion = useCallback(
    async (nextCompletionDate: string, nextQcSignOffId: string) => {
      if (!orderIdNum) return;
      setIsSavingFooter(true);
      try {
        await updateTravelerCompletion({
          orderId: orderIdNum,
          request: {
            completionDate: nextCompletionDate || null,
            qcSignOffBy: nextQcSignOffId ? parseInt(nextQcSignOffId, 10) : null,
          },
        }).unwrap();
        setToastSeverity('success');
        setToastMessage('QC Sign Off recorded successfully.');
      } catch (err) {
        logError('Failed to save traveler completion fields:', err);
        setToastSeverity('error');
        setToastMessage('Failed to save completion details. Please try again.');
      } finally {
        setIsSavingFooter(false);
      }
    },
    [orderIdNum, updateTravelerCompletion]
  );

  const handleCompletionDateChange = useCallback((date: string) => {
    setCompletionDate(date);
  }, []);

  const handleQcSignOffChange = useCallback((technicianId: string) => {
    setQcSignOffTechnicianId(technicianId);
  }, []);

  const handleSignOff = useCallback(() => {
    void persistTravelerCompletion(completionDate, qcSignOffTechnicianId);
  }, [persistTravelerCompletion, completionDate, qcSignOffTechnicianId]);

  const handleExportPdf = useCallback(async () => {
    if (!orderId) return;
    const html2pdf = (await import('html2pdf.js')).default;
    const source = document.getElementById('work-order-traveler-content');
    if (!source) return;

    // Clone the element and place it just below the visible viewport so
    // html2canvas can render it without touching the live DOM (avoids flicker).
    // Using `top: 100vh` keeps it reachable by html2canvas; `left: -9999px`
    // (fixed) is outside the composited layer and causes silent failures.
    const clone = source.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone.classList.add('wot-pdf-clone');

    const offscreen = document.createElement('div');
    offscreen.style.cssText =
      'position:fixed;top:100vh;left:0;width:' + source.offsetWidth + 'px;pointer-events:none;';
    offscreen.appendChild(clone);
    document.body.appendChild(offscreen);

    // Inject print-clean styles targeting the clone class.
    const style = document.createElement('style');
    style.textContent = `
      .wot-pdf-clone button,
      .wot-pdf-clone .MuiIconButton-root { display: none !important; }

      .wot-pdf-clone .MuiFormControl-root,
      .wot-pdf-clone .MuiTextField-root,
      .wot-pdf-clone .MuiOutlinedInput-root,
      .wot-pdf-clone .MuiInputBase-root { display: none !important; }

      .wot-pdf-clone .pdf-pending-placeholder { display: block !important; }

      /* Reveal the read-only production/QC values that mirror the hidden form
         controls so saved technician names, dates, timestamps, initials and the
         QC sign-off render in the PDF. */
      .wot-pdf-clone .pdf-complete-values { display: block !important; }
      .wot-pdf-clone .pdf-readonly-value { display: inline-block !important; }

      /* Force every truss accordion open so all production details are captured,
         even collapsed (and locked) sections. MUI keeps the content mounted but
         collapses it to height: 0 via an inline style + the hidden class. */
      .wot-pdf-clone .MuiCollapse-root {
        height: auto !important;
        min-height: 0 !important;
        visibility: visible !important;
        overflow: visible !important;
      }
      .wot-pdf-clone .MuiCollapse-wrapper,
      .wot-pdf-clone .MuiCollapse-wrapperInner { height: auto !important; }

      .wot-pdf-clone .MuiAccordion-root,
      .wot-pdf-clone .MuiAccordionSummary-root,
      .wot-pdf-clone .MuiAccordionDetails-root {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }

      .wot-pdf-clone .production-tracker-row {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }

      .wot-pdf-clone .truss-specifications-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        width: 100% !important;
      }

      .wot-pdf-clone .truss-specifications-grid .MuiTypography-root {
        word-break: break-word !important;
        overflow-wrap: anywhere !important;
        white-space: normal !important;
      }

      .wot-pdf-clone .MuiPaper-root { box-shadow: none !important; }
    `;
    document.head.appendChild(style);

    try {
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `work-order-traveler-${order?.orderNumber ?? orderId}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          ...({ pagebreak: { mode: ['css', 'legacy'] } } as Record<string, unknown>),
        })
        .from(clone)
        .save();
      setToastSeverity('success');
      setToastMessage('PDF downloaded successfully.');
    } catch (error) {
      logError('Error generating PDF for work order traveler:', error);
      setToastSeverity('error');
      setToastMessage('Failed to generate PDF. Please try again.');
    } finally {
      document.body.removeChild(offscreen);
      document.head.removeChild(style);
    }
  }, [orderId, order?.orderNumber, setToastMessage, setToastSeverity]);

  const handleAddTruss = useCallback(
    (productType: ProductType) => {
      if (!selectedBuilding || !order) return;
      // Admins always have unrestricted access to the traveler (UDOMI-965).
      if (
        !isAdmin &&
        (order.status === OrderStatus.ON_HOLD || order.status >= OrderStatus.COMPLETE)
      ) {
        setToastSeverity('error');
        setToastMessage('This order is not open for traveler edits.');
        return;
      }
      if (
        productType === 'Header' &&
        selectedBuilding.gableTrusses.length === 0 &&
        selectedBuilding.leanToTrusses.length === 0
      ) {
        setToastSeverity('error');
        setToastMessage(
          'Header truss cannot be added unless a gable or lean-to truss is added first.'
        );
        return;
      }
      setAddTrussProductType(productType);
      setAddTrussDialogOpen(true);
    },
    [selectedBuilding, order, isAdmin]
  );

  const handleDeleteTruss = useCallback((trussId: number) => {
    setTrussToDeleteId(trussId);
    setDeleteTrussConfirmOpen(true);
  }, []);

  const handleConfirmDeleteTruss = useCallback(async () => {
    if (trussToDeleteId === null || !selectedBuildingFull) return;
    const nextGable = (selectedBuildingFull.gableTrusses ?? []).filter(
      t => t.id !== trussToDeleteId
    );
    const nextHeader = (selectedBuildingFull.headerTrusses ?? []).filter(
      t => t.id !== trussToDeleteId
    );
    const nextLeanTo = (selectedBuildingFull.leanToTrusses ?? []).filter(
      t => t.id !== trussToDeleteId
    );
    const all = [...nextGable, ...nextHeader, ...nextLeanTo];
    if (all.some(t => !t.confirmedMeasurementInsidePost)) {
      setToastSeverity('error');
      setToastMessage(
        'All remaining trusses must still have confirmed inside post-to-post measurement.'
      );
      return;
    }
    if (nextHeader.length > 0 && nextGable.length === 0 && nextLeanTo.length === 0) {
      setToastSeverity('error');
      setToastMessage('Cannot remove the last gable or lean-to while header trusses remain.');
      return;
    }
    setDeleteTrussConfirmOpen(false);
    setTrussToDeleteId(null);
    await commitTrussCart(nextGable, nextHeader, nextLeanTo, {});
  }, [trussToDeleteId, selectedBuildingFull, commitTrussCart]);

  const isLoading = orderLoading || buildingsLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (currentUser?.role === UserRole.DEALER) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Work Order Travelers are not available for Dealer accounts.
        </Alert>
      </Box>
    );
  }

  if (orderError || !order || !travelerHeader) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load Work Order Traveler data. Please try again.</Alert>
      </Box>
    );
  }

  const todayFormatted = new Date().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 3 }}>
      <TravelerPageHeader
        orderId={orderId ?? ''}
        date={todayFormatted}
        onExportPdf={handleExportPdf}
        canExportPdf={canExportPdf}
      />

      <Box id="work-order-traveler-content">
        <TravelerOrderHeader order={travelerHeader} />

        {isOrderNotYetInProduction && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This order has not been released to production yet. Stage sign-offs are disabled until
            an Admin or Supervisor releases the order to production.
          </Alert>
        )}

        {isOrderProductionLocked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {order.status === OrderStatus.ON_HOLD
              ? 'This order is on hold. The traveler is read-only until the order is released.'
              : 'This order is complete (or past production). The traveler is read-only.'}
          </Alert>
        )}

        {buildingsWithoutTrusses.length > 0 && !isOrderProductionLocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {buildingsWithoutTrusses.length === 1
              ? `Building "${buildingsWithoutTrusses[0].name ?? buildingsWithoutTrusses[0].id}" has no trusses assigned. Every building must have at least one truss before the order can be completed.`
              : `${buildingsWithoutTrusses.length} buildings have no trusses assigned (${buildingsWithoutTrusses.map(b => b.name ?? b.id).join(', ')}). Every building must have at least one truss before the order can be completed.`}
          </Alert>
        )}

        {selectedBuilding && <TravelerJobDetails building={selectedBuilding} />}

        <BuildingCarousel
          buildings={
            selectedBuilding
              ? travelerBuildingsBasic.map(b =>
                  b.id === selectedBuilding.id ? selectedBuilding : b
                )
              : travelerBuildingsBasic
          }
          selectedBuildingId={selectedBuildingId}
          onSelectBuilding={setSelectedBuildingId}
        />

        {selectedBuilding && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Typography
                noWrap
                sx={{ fontSize: '13px', color: '#666', minWidth: 0, flex: 1 }}
                title={`${selectedBuilding.name}${selectedBuilding.description ? ` - ${selectedBuilding.description}` : ''}`}
              >
                {selectedBuilding.name}
                {selectedBuilding.description ? ` - ${selectedBuilding.description}` : ''}
              </Typography>
            </Box>

            {buildingDetailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <>
                <ProgressSummary building={selectedBuilding} />

                <TrussAccordionSection
                  title="Gable Trusses"
                  productType="Gable"
                  trusses={selectedBuilding.gableTrusses}
                  technicians={activeTechnicians}
                  canRecordStages={effectiveCanRecordStages}
                  canManageTrusses={effectiveCanManageTrusses}
                  canAdminOverride={effectiveCanAdminOverride}
                  onSaveStage={handleSaveStage}
                  onAddTruss={handleAddTruss}
                  onDeleteTruss={handleDeleteTruss}
                  onFinalQcComplete={handleFinalQcComplete}
                  onDirtyChange={setHasUnsavedStageEdits}
                />

                <TrussAccordionSection
                  title="Header Trusses"
                  productType="Header"
                  trusses={selectedBuilding.headerTrusses}
                  technicians={activeTechnicians}
                  canRecordStages={effectiveCanRecordStages}
                  canManageTrusses={effectiveCanManageTrusses}
                  canAdminOverride={effectiveCanAdminOverride}
                  onSaveStage={handleSaveStage}
                  onAddTruss={handleAddTruss}
                  onDeleteTruss={handleDeleteTruss}
                  onFinalQcComplete={handleFinalQcComplete}
                  onDirtyChange={setHasUnsavedStageEdits}
                />

                <TrussAccordionSection
                  title="Lean-To Trusses"
                  productType="LeanTo"
                  trusses={selectedBuilding.leanToTrusses}
                  technicians={activeTechnicians}
                  canRecordStages={effectiveCanRecordStages}
                  canManageTrusses={effectiveCanManageTrusses}
                  canAdminOverride={effectiveCanAdminOverride}
                  onSaveStage={handleSaveStage}
                  onAddTruss={handleAddTruss}
                  onDeleteTruss={handleDeleteTruss}
                  onFinalQcComplete={handleFinalQcComplete}
                  onDirtyChange={setHasUnsavedStageEdits}
                />
              </>
            )}
          </>
        )}

        {buildingsList.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
            <Typography>No buildings found for this order.</Typography>
            <Typography sx={{ fontSize: '13px', mt: 1 }}>
              Add buildings from the Order Details screen.
            </Typography>
          </Box>
        )}

        <TravelerFooter
          completionDate={completionDate}
          qcSignOffTechnicianId={qcSignOffTechnicianId}
          qcSignOffTechnicianName={qcSignOffTechnicianName}
          technicians={activeTechnicians}
          onCompletionDateChange={handleCompletionDateChange}
          onQcSignOffChange={handleQcSignOffChange}
          onSignOff={handleSignOff}
          disabled={(!isAdmin && !isSupervisor) || isOrderProductionLocked}
          saving={isSavingFooter}
          readyForSignOff={allTrussesThroughFinalQc}
          notReadyReason={
            buildingsWithoutTrusses.length > 0
              ? 'Every building must have at least one truss assigned before the work order can be QC signed off.'
              : 'All trusses must pass FINAL QC before the work order can be QC signed off.'
          }
          alreadySignedOff={orderQcSignedOff}
        />
      </Box>

      <CompletionModal
        open={!!completionModalTruss}
        truss={completionModalTruss}
        onClose={() => setCompletionModalTruss(null)}
      />

      <TravelerAddTrussDialog
        open={addTrussDialogOpen}
        productType={addTrussProductType}
        building={selectedBuildingFull ?? null}
        orderDate={order?.orderDate ?? new Date().toISOString()}
        onClose={() => {
          setAddTrussDialogOpen(false);
          setAddTrussProductType(null);
        }}
        onCommit={(g, h, l) => commitTrussCart(g, h, l, {})}
      />

      <ConfirmDialog
        open={deleteTrussConfirmOpen}
        onConfirm={() => void handleConfirmDeleteTruss()}
        onCancel={() => {
          setDeleteTrussConfirmOpen(false);
          setTrussToDeleteId(null);
        }}
        title="Remove truss"
        message="Remove this truss from the building? Associated production tracking may be affected. This cannot be undone from the traveler."
        confirmText="Remove"
        cancelText="Cancel"
        confirmColor="error"
      />

      <Dialog
        open={postSizeWarning.show}
        onClose={() => setPostSizeWarning({ show: false, message: '', pendingLists: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          Post size warning
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {postSizeWarning.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Truss US Engineering recommends 8x8 posts for spans beyond the 6x6 limit for this
            classification. You can proceed with 6x6 posts if you confirm this configuration meets
            your engineering requirements.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPostSizeWarning({ show: false, message: '', pendingLists: null })}
          >
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handlePostSizeProceed}>
            Proceed with 6x6 posts
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={toastSeverity}
          onClose={() => setToastMessage(null)}
          sx={{ fontWeight: 600 }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={showUnsavedChangesDialog}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Unsaved Changes"
        message="You have unsaved production stage data. Are you sure you want to leave? Your changes will be lost."
        confirmText="Leave"
        cancelText="Stay"
        confirmColor="warning"
      />
    </Box>
  );
};

export default WorkOrderTravelerPage;
